import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { LOGOUT } from '../../constants/actions/auth.constants';
import type { AppState } from '../../reducers/root.reducer';
import RegistrationService from '../../services/common/RegistrationService';
import IRegistration from '../../types/auth/IRegistration';

const NAME = 'Registration';

const getRegistration = createAsyncThunk(
    `${NAME}/getRegistration`,
    RegistrationService.getRegistration,
);
const postRegistration = createAsyncThunk(
    `${NAME}/postRegistration`,
    RegistrationService.postRegistration,
);

export type RegistrationState = {
    data: IRegistration | undefined;
    loading: boolean;
    sending: boolean;
    email: string | undefined;
    urlToken: string | undefined;
    error: boolean;
};

const initialState: RegistrationState = {
    data: undefined,
    loading: false,
    sending: false,
    email: undefined,
    urlToken: undefined,
    error: false,
};

const slice = createSlice({
    name: NAME,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getRegistration.pending, (state) => {
            state.loading = true;
            state.error = false;
        });
        builder.addCase(getRegistration.fulfilled, (state, action) => {
            state.data = action.payload?.data;
        });
        builder.addCase(getRegistration.rejected, (state) => {
            state.error = true;
        });

        builder.addCase(postRegistration.pending, (state) => {
            state.sending = true;
        });
        builder.addCase(postRegistration.fulfilled, (state, action) => {
            if (action?.payload?.data) {
                const { email, urlToken } = action.payload.data;
                state.email = email;
                state.urlToken = urlToken;
            }
        });

        builder.addCase(LOGOUT, () => initialState);

        builder.addMatcher(
            isAnyOf(getRegistration.fulfilled, getRegistration.rejected),
            (state: RegistrationState) => {
                state.loading = false;
            },
        );

        builder.addMatcher(
            isAnyOf(postRegistration.fulfilled, postRegistration.rejected),
            (state: RegistrationState) => {
                state.sending = false;
            },
        );
        builder.addMatcher(
            isAnyOf(postRegistration.pending, postRegistration.rejected),
            (state: RegistrationState) => {
                state.email = undefined;
                state.urlToken = undefined;
            },
        );
    },
});

const state = (state: AppState) => state.common.auth.registration;

const RegistrationSlice = {
    reducer: slice.reducer,
    actions: {
        getRegistration,
        postRegistration,
    },
    state: {
        getAll: state,
    },
};

export default RegistrationSlice;
